'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  Menu, Bell, Search, ChevronDown, PanelLeftClose, PanelLeftOpen,
  User, Settings, LogOut, Shield, TrendingUp, Building2,
  BarChart3, DollarSign, LayoutDashboard, Globe
} from 'lucide-react';

interface TopbarProps {
  portalName: string;
  portalRole: string;
  onMenuToggle: () => void;
  onSidebarToggle: () => void;
  sidebarCollapsed: boolean;
}

const portals = [
  { label: 'Investor Portal', href: '/', role: 'investor', icon: TrendingUp, color: 'text-primary' },
  { label: 'Issuer Portal', href: '/issuer-portal', role: 'issuer', icon: Building2, color: 'text-accent' },
  { label: 'Platform Admin', href: '/platform-administration-portal', role: 'admin', icon: LayoutDashboard, color: 'text-warning' },
  { label: 'Compliance', href: '/compliance-portal', role: 'compliance', icon: Shield, color: 'text-info' },
  { label: 'Risk', href: '/risk-portal', role: 'risk', icon: BarChart3, color: 'text-negative' },
  { label: 'Finance', href: '/finance-portal', role: 'finance', icon: DollarSign, color: 'text-positive' },
  { label: 'Public Landing', href: '/landing', role: 'landing', icon: Globe, color: 'text-cyan-400' },
];

export default function Topbar({ portalName, portalRole, onMenuToggle, onSidebarToggle, sidebarCollapsed }: TopbarProps) {
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const pathname = usePathname();

  const currentPortal = portals.find(p => p.role === portalRole) || portals[0];

  return (
    <header className="h-14 bg-surface-2 border-b border-border flex items-center gap-3 px-4 flex-shrink-0 relative z-30">
      {/* Mobile menu toggle */}
      <button onClick={onMenuToggle} className="btn-ghost lg:hidden p-1.5">
        <Menu size={20} />
      </button>

      {/* Desktop sidebar toggle */}
      <button onClick={onSidebarToggle} className="btn-ghost hidden lg:flex p-1.5" title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
      </button>

      {/* Portal switcher */}
      <div className="relative">
        <button
          onClick={() => { setPortalMenuOpen(!portalMenuOpen); setNotifOpen(false); setUserMenuOpen(false); }}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-3 border border-border hover:border-primary/40 transition-all duration-150 text-sm font-medium"
        >
          <currentPortal.icon size={15} className={currentPortal.color} />
          <span className="hidden sm:block text-foreground">{portalName}</span>
          <ChevronDown size={14} className="text-muted-foreground" />
        </button>

        {portalMenuOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-surface-3 border border-border rounded-xl shadow-elevated py-1 z-50 animate-scale-in">
            <p className="section-header px-3 pt-2">Switch Portal</p>
            {portals.map((portal) => (
              <Link
                key={`portal-switch-${portal.role}`}
                href={portal.href}
                onClick={() => setPortalMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-surface-4 transition-colors ${
                  pathname === portal.href || (portal.href !== '/' && pathname.startsWith(portal.href))
                    ? 'text-primary bg-primary/5' :'text-secondary-foreground'
                }`}
              >
                <portal.icon size={15} className={portal.color} />
                {portal.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 flex-1 max-w-xs">
        <div className="relative w-full">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search campaigns, investors..."
            className="input-field pl-8 py-1.5 text-sm h-8"
          />
        </div>
      </div>

      <div className="flex-1" />

      {/* Mobile search icon */}
      <button className="btn-ghost p-1.5 md:hidden">
        <Search size={18} />
      </button>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => { setNotifOpen(!notifOpen); setPortalMenuOpen(false); setUserMenuOpen(false); }}
          className="btn-ghost p-1.5 relative"
        >
          <Bell size={18} />
          <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-critical" />
        </button>

        {notifOpen && (
          <div className="absolute top-full right-0 mt-1 w-80 bg-surface-3 border border-border rounded-xl shadow-elevated z-50 animate-scale-in">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="font-semibold text-sm">Notifications</span>
              <span className="status-badge badge-critical">5 new</span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {[
                { msg: 'KYC review required for Thornfield Capital Ltd', time: '2m ago', type: 'warning' },
                { msg: 'Campaign CVT-2024-089 reached 80% funding target', time: '18m ago', type: 'info' },
                { msg: 'AML alert triggered — Transaction TXN-884921', time: '45m ago', type: 'critical' },
                { msg: 'Settlement batch SB-20260820 processed successfully', time: '1h ago', type: 'positive' },
                { msg: 'New investor onboarding: Meridian Pension Fund', time: '2h ago', type: 'info' },
              ].map((n, i) => (
                <div key={`notif-${i}`} className="px-4 py-3 border-b border-border/50 hover:bg-surface-4 cursor-pointer transition-colors">
                  <p className="text-sm text-foreground">{n.msg}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.time}</p>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5">
              <button className="text-xs text-primary hover:text-primary/80 font-medium">View all notifications</button>
            </div>
          </div>
        )}
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => { setUserMenuOpen(!userMenuOpen); setPortalMenuOpen(false); setNotifOpen(false); }}
          className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-surface-3 transition-colors"
        >
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
            <User size={14} className="text-primary" />
          </div>
          <div className="hidden sm:block text-left">
            <p className="text-xs font-semibold text-foreground leading-none">Alexandra Voss</p>
            <p className="text-xs text-muted-foreground mt-0.5 capitalize">{portalRole} Officer</p>
          </div>
          <ChevronDown size={13} className="text-muted-foreground hidden sm:block" />
        </button>

        {userMenuOpen && (
          <div className="absolute top-full right-0 mt-1 w-48 bg-surface-3 border border-border rounded-xl shadow-elevated py-1 z-50 animate-scale-in">
            <div className="px-3 py-2 border-b border-border mb-1">
              <p className="text-sm font-semibold text-foreground">Alexandra Voss</p>
              <p className="text-xs text-muted-foreground">a.voss@crowdvault.io</p>
            </div>
            <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-secondary-foreground hover:bg-surface-4 hover:text-foreground transition-colors">
              <User size={14} /> My Profile
            </button>
            <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-secondary-foreground hover:bg-surface-4 hover:text-foreground transition-colors">
              <Settings size={14} /> Preferences
            </button>
            <div className="border-t border-border mt-1 pt-1">
              <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-negative/80 hover:text-negative hover:bg-surface-4 transition-colors">
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}